import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import useSpotifyTracks from "../hooks/useSpotifyTracks";
import { CurrentlyPlayingState } from "../types/currentlyPlayingState";

interface TrackRecommendationsState {
  isFetching: boolean;
  hasData: boolean;
  currentSongId: string | null;
  hasLiveTracks: boolean;
  hasCoverTracks: boolean;
  tracks: any[];
}

const TrackRecommendationsContext = createContext<TrackRecommendationsState>({
  isFetching: false,
  hasData: false,
  currentSongId: null,
  hasLiveTracks: false,
  hasCoverTracks: false,
  tracks: []
});

interface TrackRecommendationsProviderProps {
  children: ReactNode;
  currentlyPlayingSong: CurrentlyPlayingState;
}

export const TrackRecommendationsProvider: React.FC<TrackRecommendationsProviderProps> = ({
  children,
  currentlyPlayingSong
}) => {
  const { searchLiveTracks, searchCoverTracks, searchTopTracks } = useSpotifyTracks();
  const isFetchingRef = useRef(false);
  const lastProcessedSongRef = useRef<string>("");
  const searchFunctionsRef = useRef({ searchTopTracks, searchLiveTracks, searchCoverTracks });

  // Update ref when functions change
  useEffect(() => {
    searchFunctionsRef.current = { searchTopTracks, searchLiveTracks, searchCoverTracks };
  }, [searchTopTracks, searchLiveTracks, searchCoverTracks]);

  const [state, setState] = useState<TrackRecommendationsState>({
    isFetching: false,
    hasData: false,
    currentSongId: null,
    hasLiveTracks: false,
    hasCoverTracks: false,
    tracks: []
  });

  useEffect(() => {
    const fetchTrackRecommendations = async () => {
      // Don't fetch if no song is playing
      if (!currentlyPlayingSong?.currentlyPlayingObject?.item) {
        return;
      }

      const song = currentlyPlayingSong.currentlyPlayingObject.item;
      const songId = song.id;

      // Prevent multiple API calls for the same song
      if (lastProcessedSongRef.current === songId || isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      lastProcessedSongRef.current = songId;

      setState(prev => ({
        ...prev,
        isFetching: true,
        currentSongId: songId
      }));

      try {
        const artist = song.artists.map(a => a.name).join(", ");
        const songName = song.name;

        console.log(
          `Background fetch: Getting track recommendations for "${songName}" by ${artist}`
        );

        // Fetch all track types in parallel
        const [topTracks, liveTracks, coverTracks] = await Promise.all([
          searchFunctionsRef.current.searchTopTracks(songName, artist),
          searchFunctionsRef.current.searchLiveTracks(songName, artist),
          searchFunctionsRef.current.searchCoverTracks(songName, artist)
        ]);

        setState(prev => ({
          ...prev,
          isFetching: false,
          hasData: true,
          hasLiveTracks: liveTracks.length > 0,
          hasCoverTracks: coverTracks.length > 0,
          tracks: [topTracks, liveTracks, coverTracks]
        }));

        console.log(
          `Background fetch: Found ${topTracks.length} top tracks, ${liveTracks.length} live tracks, ${coverTracks.length} cover tracks`
        );
      } catch (error) {
        console.error("Background track recommendations fetch error:", error);
        setState(prev => ({
          ...prev,
          isFetching: false,
          hasData: false,
          hasLiveTracks: false,
          hasCoverTracks: false,
          tracks: []
        }));
      } finally {
        isFetchingRef.current = false;
      }
    };

    // Fetch when song changes
    if (currentlyPlayingSong?.currentlyPlayingObject?.item) {
      fetchTrackRecommendations();
    }
  }, [currentlyPlayingSong?.currentlyPlayingObject?.item?.id]);

  // Reset state when song changes
  useEffect(() => {
    const currentSongId = currentlyPlayingSong?.currentlyPlayingObject?.item?.id;

    if (currentSongId && currentSongId !== state.currentSongId) {
      setState(prev => ({
        ...prev,
        hasData: false,
        hasLiveTracks: false,
        hasCoverTracks: false,
        tracks: [],
        currentSongId: currentSongId
      }));
      lastProcessedSongRef.current = "";
      isFetchingRef.current = false;
    }
  }, [currentlyPlayingSong?.currentlyPlayingObject?.item?.id, state.currentSongId]);

  return (
    <TrackRecommendationsContext.Provider value={state}>
      {children}
    </TrackRecommendationsContext.Provider>
  );
};

export const useTrackRecommendations = () => {
  return useContext(TrackRecommendationsContext);
};
